// Per-character CSS variables. Every screen sets them on its root through <Screen theme={...}>.
// --acc (accent), --on (text on accent), --bg (page background), --acc-glow (accent at ~0.3 alpha).
import type { JSX } from 'preact';
import type { CharId } from '@shared/types';
import { CHAR_IDS } from '@shared/types';
import { C, PRE } from '@shared/content/characters';

/** 'pre' = before a buddy is picked (O1-O7): warm gold on #100E0C. */
export type Theme = CharId | 'pre';

export interface ThemeColors { acc: string; on: string; bg: string; glow: string }

export function rgba(hex: string, a: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

export function colors(theme: Theme): ThemeColors {
  const t = theme === 'pre' ? PRE : C[theme];
  return { acc: t.acc, on: t.on, bg: t.bg, glow: rgba(t.acc, theme === 'arjun' ? 0.3 : 0.32) };
}

/** Inline style object with the CSS variables for a theme. */
export function themeVars(theme: Theme): JSX.CSSProperties {
  const c = colors(theme);
  return {
    '--acc': c.acc, '--on': c.on, '--bg': c.bg, '--acc-glow': c.glow,
    // progress-bar glow: the prototype uses 0.7 alpha before a buddy is picked, the full accent after
    '--acc-glow-strong': theme === 'pre' ? rgba(c.acc, 0.7) : c.acc,
    // tints for selected options / tags
    '--acc-a12': rgba(c.acc, 0.12), '--acc-a16': rgba(c.acc, 0.16), '--acc-a18': rgba(c.acc, 0.18), '--acc-a25': rgba(c.acc, 0.25),
  } as JSX.CSSProperties;
}

export const isCharId = (x: unknown): x is CharId => typeof x === 'string' && (CHAR_IDS as string[]).includes(x);
